import { useState, useRef, useEffect, useCallback } from 'react'
import PersonaAvatar from './PersonaAvatar'

interface Slide {
  icon?: string
  avatar?: string
  avatarBorder?: string
  title: string
  body: string
  narrator: string
  showTeam?: boolean
  enterBtn?: boolean
  audio?: string
  video?: string
}

interface SlideshowConfig {
  teamNames: string[]
  teamDescs: Record<string, string>
  slides: Slide[]
  welcomeConvId?: string
}

interface Props {
  config: SlideshowConfig
  communitySlug: string
  communityName: string
  onEnter: () => void
}

export default function WelcomeSlideshow({ config, communitySlug, communityName, onEnter }: Props) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [visible, setVisible] = useState(true)
  const [fading, setFading] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const [speed, setSpeed] = useState(() => {
    const saved = localStorage.getItem('slide-audio-speed')
    return saved ? parseFloat(saved) : 1.3
  })

  const slide = config.slides[currentSlide]
  const isLast = currentSlide === config.slides.length - 1

  // Load audio when slide changes — user clicks play manually
  useEffect(() => {
    if (slide?.audio && audioRef.current) {
      audioRef.current.src = slide.audio
      audioRef.current.playbackRate = speed
    }
  }, [currentSlide, slide?.audio, speed])

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next() }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev() }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  })

  const next = useCallback(() => {
    if (currentSlide < config.slides.length - 1) setCurrentSlide(s => s + 1)
  }, [currentSlide, config.slides.length])

  const prev = useCallback(() => {
    if (currentSlide > 0) setCurrentSlide(s => s - 1)
  }, [currentSlide])

  function handleEnter() {
    if (audioRef.current) audioRef.current.pause()
    localStorage.setItem('community_welcomed_' + communitySlug, Date.now().toString())
    setFading(true)
    setTimeout(() => {
      setVisible(false)
      onEnter()
    }, 800)
  }

  function handleSpeedChange(spd: number) {
    setSpeed(spd)
    localStorage.setItem('slide-audio-speed', String(spd))
    if (audioRef.current) audioRef.current.playbackRate = spd
  }

  if (!visible) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        background: 'radial-gradient(ellipse at center, #0F1B2D 0%, #080E1A 70%)',
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.8s ease',
      }}
    >
      <div className="flex flex-col items-center gap-6 max-w-lg w-full px-8">
        {/* Slide content */}
        <div className="text-center" key={currentSlide} style={{ animation: 'slideFadeIn 0.5s ease-out' }}>
          {/* Icon or Avatar */}
          {slide.avatar ? (
            <img
              src={slide.avatar}
              alt=""
              className="mx-auto mb-4 rounded-full object-cover"
              style={{ width: 80, height: 80, border: `3px solid ${slide.avatarBorder || 'var(--accent)'}` }}
            />
          ) : slide.icon ? (
            <div className="text-4xl mb-4">{slide.icon}</div>
          ) : null}

          {/* Title */}
          <h1 className="text-xl font-bold mb-4" style={{ color: '#F0F4F8', letterSpacing: '-0.5px' }}>
            {slide.title}
          </h1>

          {/* Body */}
          <p className="text-sm leading-relaxed" style={{ color: '#8B9DAF' }}>
            {slide.body.replace('{{communityName}}', communityName)}
          </p>

          {/* Video player */}
          {slide.video && (
            <div className="mt-6 w-full max-w-lg mx-auto" style={{ borderRadius: 12, overflow: 'hidden' }}>
              <video
                controls
                preload="metadata"
                className="w-full"
                style={{ borderRadius: 12, maxHeight: 360 }}
                poster=""
              >
                <source src={slide.video} type="video/mp4" />
              </video>
            </div>
          )}

          {/* Team cards */}
          {slide.showTeam && (
            <div className="flex justify-center gap-4 mt-6">
              {config.teamNames.map(name => (
                <div key={name} className="flex flex-col items-center gap-2">
                  <PersonaAvatar name={name} size={48} />
                  <div className="text-xs font-semibold capitalize" style={{ color: '#F0F4F8' }}>
                    {name}
                  </div>
                  <div className="text-xs" style={{ color: '#5A6B7F' }}>
                    {config.teamDescs[name] || ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Enter button */}
          {slide.enterBtn && (
            <button
              onClick={handleEnter}
              className="mt-6 px-8 py-3 rounded-lg text-sm font-semibold transition-transform hover:scale-105"
              style={{ background: 'var(--accent)', color: '#0A1628' }}
            >
              Step Inside
            </button>
          )}

          {/* Narrator */}
          <div className="text-xs mt-4" style={{ color: '#5A6B7F' }}>
            Narrated by {slide.narrator}
          </div>
        </div>

        {/* Audio player — only shown when slide has audio */}
        {slide.audio && (
          <div className="flex flex-col items-center gap-2">
            <audio ref={audioRef} controls preload="auto" className="w-full max-w-sm" style={{ height: 36, borderRadius: 8 }} />
            <div className="flex gap-1.5">
              {[1, 1.3, 1.5, 2].map(spd => (
                <button
                  key={spd}
                  onClick={() => handleSpeedChange(spd)}
                  className="text-xs px-2.5 py-1 rounded"
                  style={{
                    background: speed === spd ? 'var(--accent)' : '#1E3048',
                    color: speed === spd ? '#fff' : '#8B9DAF',
                    border: `1px solid ${speed === spd ? 'var(--accent)' : '#2D4A6A'}`,
                    cursor: 'pointer',
                  }}
                >
                  {spd}x
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center gap-4">
          <button
            onClick={prev}
            disabled={currentSlide === 0}
            className="text-sm px-3 py-1.5 rounded disabled:opacity-30"
            style={{ background: 'none', border: '1px solid #2D4A6A', color: '#8B9DAF', cursor: 'pointer' }}
          >
            ←
          </button>

          {/* Dots */}
          <div className="flex gap-2">
            {config.slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className="rounded-full transition-all"
                style={{
                  width: i === currentSlide ? 24 : 8,
                  height: 8,
                  background: i === currentSlide ? 'var(--accent)' : '#2D4A6A',
                  border: 'none',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>

          <button
            onClick={isLast ? handleEnter : next}
            className="text-sm px-3 py-1.5 rounded"
            style={{ background: 'none', border: '1px solid #2D4A6A', color: '#8B9DAF', cursor: 'pointer' }}
          >
            {isLast ? 'Enter' : '→'}
          </button>
        </div>
      </div>

      {/* Fade-in animation */}
      <style>{`
        @keyframes slideFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
